import { CircuitDayFormData } from '@/types/circuits/CircuitDaysFormData';
import { addDays, isBefore } from 'date-fns';
import { CircuitDayPrices } from './computeBenefitPrices';
import { getMaxPricingDate } from './pricingHorizon';
import { storedDateToLocalDate } from './priceDateKey';

type Step = CircuitDayFormData['steps'][number];
type StepElement = Step['transport'];
type Benefit = NonNullable<StepElement['standardBenefit']>;
type StepElementPrices = CircuitDayPrices[number]['transport'];

const STEP_ELEMENT_KEYS = [
  'transport',
  'activities',
  'meals',
  'accommodations',
] as const;

const getBenefitLastTariffDate = (benefit: Benefit) => {
  const tarifs = [
    ...(benefit.Pricing ?? []),
    ...(benefit.hotel?.HotelRoomCategory?.flatMap(
      (category) => category.pricings ?? [],
    ) ?? []),
    ...(benefit.carRental?.carCategories?.flatMap(
      (category) => category.pricings ?? [],
    ) ?? []),
  ];
  const endTimes = tarifs.flatMap((tarif) =>
    (tarif.periods ?? []).map((period) =>
      storedDateToLocalDate(period.endDate).getTime(),
    ),
  );
  return endTimes.length ? new Date(Math.max(...endTimes)) : null;
};

/**
 * Dernière date de départ pour laquelle toutes les prestations standard ont
 * encore une période de tarif, lue directement dans les périodes (sans calcul
 * jour par jour). Les trous à l'intérieur des périodes restent possibles : ces
 * dates s'afficheront « — ». Renvoie null si une prestation n'a aucun tarif.
 */
export const getLastPricedStartDate = (
  circuitDays: { steps: Step[] }[],
): Date | null => {
  let lastStartDate = getMaxPricingDate();

  for (const [indexDay, day] of circuitDays.entries()) {
    for (const step of day.steps) {
      for (const key of STEP_ELEMENT_KEYS) {
        const benefit = step[key]?.standardBenefit;
        if (!benefit) continue;
        const lastTariffDate = getBenefitLastTariffDate(benefit);
        if (!lastTariffDate) return null;
        const benefitLastStartDate = addDays(lastTariffDate, -indexDay);
        if (isBefore(benefitLastStartDate, lastStartDate))
          lastStartDate = benefitLastStartDate;
      }
    }
  }

  return lastStartDate;
};

const mergeBenefitPrices = <T extends { prices: Map<string, unknown> }>(
  base: T | undefined,
  extension: T | undefined,
): T | undefined =>
  base && extension
    ? { ...base, prices: new Map([...base.prices, ...extension.prices]) }
    : base;

const mergeStepElementPrices = (
  base: StepElementPrices,
  extension: StepElementPrices,
): StepElementPrices =>
  base && extension
    ? {
        ...base,
        standardBenefit: mergeBenefitPrices(
          base.standardBenefit,
          extension.standardBenefit,
        ),
        optionalBenefits: base.optionalBenefits?.map(
          (benefit, index) =>
            mergeBenefitPrices(benefit, extension.optionalBenefits?.[index]) ??
            benefit,
        ),
      }
    : base;

/**
 * Fusionne les prix d'une tranche de dates supplémentaire dans les prix déjà
 * calculés. Le prix d'une date ne dépend que des tarifs valides ce jour-là,
 * donc calculer une tranche à part donne les mêmes valeurs qu'un calcul complet.
 */
export const mergeCircuitDaysPrices = (
  base: CircuitDayPrices[],
  extension: CircuitDayPrices[],
): CircuitDayPrices[] =>
  base.map((day, indexDay) =>
    day.map((step, indexStep) => {
      const extensionStep = extension[indexDay]?.[indexStep];
      return {
        transport: mergeStepElementPrices(
          step.transport,
          extensionStep?.transport,
        ),
        activities: mergeStepElementPrices(
          step.activities,
          extensionStep?.activities,
        ),
        meals: mergeStepElementPrices(step.meals, extensionStep?.meals),
        accommodations: mergeStepElementPrices(
          step.accommodations,
          extensionStep?.accommodations,
        ),
      };
    }),
  );
